/**
 * El aviso de estado de la conexión.
 *
 * **Está siempre visible, no solo cuando falla.** En un aula la wifi se cae constantemente, y la
 * pregunta que se hace quien está delante del móvil es «¿estoy dentro?». Un indicador que solo
 * aparece al romperse deja esa pregunta sin responder el resto del tiempo.
 *
 * No comunica nada solo por color (principio IV): lleva símbolo, texto y color, los tres.
 */

import { useTranslation } from 'react-i18next';
import type { ConnectionStatus } from '../game/socket.js';

const MARKS: Readonly<Record<ConnectionStatus, string>> = {
  connecting: '◌',
  open: '●',
  reconnecting: '◑',
  closed: '○',
};

const TONES: Readonly<Record<ConnectionStatus, string>> = {
  connecting: 'text-text-dim',
  open: 'text-ok',
  // Tinta y no el acento: el acento sobre papel da 1,16:1 y sencillamente no se ve. La forma
  // del glifo —el círculo a medias— es la que lleva el significado.
  reconnecting: 'text-text',
  closed: 'text-text-dim',
};

export function EstadoConexion({ status }: { status: ConnectionStatus }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <p
      // `polite` y no `assertive`: una reconexión no debe cortar lo que el lector esté leyendo.
      role="status"
      aria-live="polite"
      className={`flex items-center gap-2 text-sm ${TONES[status]}`}
    >
      <span aria-hidden="true">{MARKS[status]}</span>
      <span>{t(`game.connection.${status}`)}</span>
    </p>
  );
}
